// Consult Ops queue (nsofar docs/33) — age-ordered, oldest first.
//
// There is deliberately NO SLA on consult fulfillment (docs/32 §5 founder
// decision): request age — not a deadline — is the ops visibility mechanism,
// so the default sort is created_at ASC and the leading column is Age.
// Distinct from TeleconsultPage (clinical per-patient consult records): this
// queue is the ops console over nsofar-synced tele_consult SRs.

import { useMemo, useState } from "react";
import type { JSX } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import type { ConsultBlock } from "@hcm/core";
import { apiFetch } from "../lib/api.js";
import { Shell } from "../components/Shell.js";
import { ConsultStateBadge } from "../components/consult/ConsultStateBadge.js";
import { formatAge } from "../lib/consultOps.js";
import { CHOSEN_VIA_LABELS } from "@hcm/core";
import {
  DataTable, useDataTable, type ColumnDef, type ListQuery,
} from "../components/DataTable/index.js";

interface ConsultSrRow {
  _id: string;
  status: string;
  created_at: string;
  nsofar_sr_id?: string | null;
  patient_id: string;
  patient_name?: string | null;
  consult?: ConsultBlock | null;
}

interface ConsultListResponse { data: ConsultSrRow[]; total: number; page: number; limit: number; }

// Open/Closed tabs filter on the DERIVED coarse SR status (written by the
// sync receiver); the §2 chip carries the real lifecycle state per row.
const TABS = [
  { label: "Open",   value: "new,in_progress,scheduled" },
  { label: "Closed", value: "completed,cancelled" },
  { label: "All",    value: "" },
];

function buildQs(query: ListQuery, statusFilter: string): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v == null || v === "") continue;
    p.set(k, String(v));
  }
  p.set("service_type", "tele_consult");
  if (statusFilter) p.set("status", statusFilter);
  return p.toString();
}

function entitlementLabel(consult?: ConsultBlock | null): string {
  const e = consult?.entitlement;
  if (!e) return "—";
  return e.outcome === "included" ? `Included${e.plan_key ? ` · ${e.plan_key}` : ""}` : "À la carte";
}

function buildColumns(navigate: ReturnType<typeof useNavigate>): ReadonlyArray<ColumnDef<ConsultSrRow>> {
  return [
    { accessorKey: "created_at", header: "Age", sortable: true,
      cell: (sr) => <span className="text-sm font-medium text-slate-700 whitespace-nowrap">{formatAge(sr.created_at)}</span> },
    { accessorKey: "nsofar_sr_id", header: "nsofar ref",
      cell: (sr) => <span className="font-mono text-xs text-slate-500">{sr.nsofar_sr_id ?? "—"}</span> },
    { accessorKey: "patient_name", header: "Patient",
      cell: (sr) => <span className="text-slate-700">{sr.patient_name ?? `P-${sr.patient_id.slice(-5).toLowerCase()}`}</span> },
    { accessorKey: "specialty", header: "Specialty",
      cell: (sr) => <span className="text-slate-700">{sr.consult?.specialty || "—"}</span> },
    { accessorKey: "mode", header: "Mode",
      cell: (sr) => <span className="text-slate-500">{sr.consult?.mode ?? "—"}</span> },
    { accessorKey: "consult_state", header: "State",
      cell: (sr) => <ConsultStateBadge status={sr.consult?.status ?? null} /> },
    { accessorKey: "entitlement", header: "Entitlement",
      cell: (sr) => <span className="text-xs text-slate-500">{entitlementLabel(sr.consult)}</span> },
    { accessorKey: "flags", header: "Flags",
      cell: (sr) => (
        <span className="text-xs text-slate-500">
          {[sr.consult?.auto_confirm ? "auto-confirm" : null,
            sr.consult?.chosen_via ? (CHOSEN_VIA_LABELS[sr.consult.chosen_via] ?? sr.consult.chosen_via) : null,
          ].filter(Boolean).join(" · ") || "—"}
        </span>
      ) },
    { accessorKey: "actions", header: "", align: "right",
      cell: (sr) => (
        <button type="button" onClick={() => navigate(`/sr/${sr._id}`)}
          aria-label={`Open consult ${sr.nsofar_sr_id ?? sr._id}`}
          className="text-sm text-navy-800 hover:text-navy-950 underline focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500 rounded">
          Open
        </button>
      ) },
  ];
}

export function ConsultOpsPage(): JSX.Element {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState(TABS[0]?.value ?? "");
  const tableState = useDataTable({
    pageSize: 20,
    defaultSort: { sortBy: "created_at", order: "asc" }, // oldest first — no SLA by design
  });
  const columns = useMemo(() => buildColumns(navigate), [navigate]);
  const qs = buildQs(tableState.query, statusFilter);
  const { data, isLoading, isError } = useQuery<ConsultListResponse>({
    queryKey: ["consult-ops-queue", qs],
    queryFn: () => apiFetch<ConsultListResponse>(`/sr?${qs}`),
    refetchInterval: 30_000,
  });
  const rows = data?.data ?? [];
  const total = data?.total ?? 0;

  return (
    <Shell>
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold text-slate-900">
            Consult Ops <span className="text-sm font-normal text-slate-400">({total})</span>
          </h1>
          <p className="text-xs text-slate-400">Oldest first — consults are driven from the SR detail page.</p>
        </div>
        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit">
          {TABS.map((t) => (
            <button key={t.label} onClick={() => { setStatusFilter(t.value); tableState.setPage(1); }}
              className={`text-xs font-medium px-3 py-1.5 rounded-md transition-colors ${statusFilter === t.value ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
              {t.label}
            </button>
          ))}
        </div>
        <DataTable<ConsultSrRow>
          state={tableState}
          columns={columns}
          data={rows}
          total={total}
          rowKey={(sr) => sr._id}
          ariaLabel="Consult ops queue"
          isLoading={isLoading}
          isError={isError}
          errorMessage="Failed to load the consult queue."
          emptyState="No doctor consults synced yet."
          searchPlaceholder="Search by ref"
        />
      </div>
    </Shell>
  );
}
